import { useEffect, useRef } from "react";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,600;1,300;1,600&family=DM+Sans:wght@300;400;500&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { overflow-x: hidden; }
`;

// ── Keyframes
const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.5; transform: scale(1); }
  50%       { opacity: 1;   transform: scale(1.06); }
`;

const floatY = keyframes`
  0%, 100% { transform: translateY(0px) rotate(var(--r)); }
  50%       { transform: translateY(-10px) rotate(var(--r)); }
`;

// ── Layout
const Wrapper = styled.div`
  position: relative;
`;

const Sticky = styled.div`
  position: sticky;
  top: 0;
  height: 100vh;
  overflow: hidden;
  background: linear-gradient(
    160deg,
    #0a000f 0%,
    #130025 40%,
    #200040 70%,
    #0f0020 100%
  );
  display: flex;
  align-items: center;
  justify-content: center;
`;

// Ambient orbs
const Orb = styled.div`
  position: absolute;
  border-radius: 50%;
  filter: blur(90px);
  pointer-events: none;
  opacity: ${({ op }) => op || 0.3};
  animation: ${pulse} ${({ dur }) => dur || "5s"} ease-in-out infinite;
  animation-delay: ${({ del }) => del || "0s"};
  width: ${({ s }) => s};
  height: ${({ s }) => s};
  background: ${({ c }) => c};
  top: ${({ top }) => top};
  left: ${({ left }) => left};
  right: ${({ right }) => right};
  bottom: ${({ bottom }) => bottom};
`;

// ── Scene
const Scene = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// ── Left: card stack area
const StackArea = styled.div`
  position: relative;
  width: 340px;
  height: 480px;
  flex-shrink: 0;
`;

// ── Individual card
const Card = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 28px;
  overflow: hidden;
  transform-origin: bottom center;
  cursor: pointer;
  will-change: transform, opacity, filter;

  /* Each card has a CSS var for its resting rotation */
  --r: ${({ rot }) => rot || "0deg"};
  transform: rotate(var(--r)) translateY(${({ oy }) => oy || "0px"});
`;

const CardBg = styled.div`
  position: absolute;
  inset: 0;
  background: ${({ bg }) => bg};
  transition: filter 0.3s;
`;

const CardNoise = styled.div`
  position: absolute;
  inset: 0;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E");
  background-size: cover;
  mix-blend-mode: overlay;
  pointer-events: none;
`;

const CardOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 35%,
    rgba(0, 0, 0, 0.75) 100%
  );
`;

const CardBlur = styled.div`
  position: absolute;
  inset: 0;
  backdrop-filter: blur(${({ b }) => b || "0px"});
  background: rgba(0, 0, 0, ${({ d }) => d || "0"});
  transition: backdrop-filter 0.4s;
`;

const CardContent = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 1.75rem;
`;

const CardName = styled.div`
  font-family: "Cormorant Garamond", serif;
  font-size: 2rem;
  font-weight: 600;
  color: rgba(255, 255, 255, ${({ op }) => op || 0.4});
  line-height: 1;
  margin-bottom: 0.3rem;
  transition: color 0.5s;
`;

const CardMeta = styled.div`
  font-family: "DM Sans", sans-serif;
  font-size: 0.8rem;
  font-weight: 300;
  color: rgba(255, 255, 255, ${({ op }) => op || 0.2});
  letter-spacing: 0.05em;
  transition: color 0.5s;
`;

const CardTags = styled.div`
  display: flex;
  gap: 0.4rem;
  margin-top: 0.75rem;
  flex-wrap: wrap;
  opacity: ${({ op }) => op || 0};
  transform: translateY(${({ ty }) => ty || "8px"});
  transition:
    opacity 0.5s,
    transform 0.5s;
`;

const Tag = styled.span`
  font-family: "DM Sans", sans-serif;
  font-size: 0.65rem;
  font-weight: 500;
  letter-spacing: 0.08em;
  padding: 0.25rem 0.6rem;
  border-radius: 100px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.7);
  text-transform: uppercase;
`;

// Match badge that appears on top card
const MatchBadge = styled.div`
  position: absolute;
  top: 1.25rem;
  right: 1.25rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  background: linear-gradient(
    135deg,
    rgba(232, 121, 249, 0.2),
    rgba(124, 58, 237, 0.2)
  );
  border: 1px solid rgba(232, 121, 249, 0.5);
  border-radius: 100px;
  padding: 0.35rem 0.8rem;
  opacity: ${({ op }) => op || 0};
  transform: translateY(${({ ty }) => ty || "-8px"});
  transition:
    opacity 0.5s,
    transform 0.5s;

  span {
    font-family: "DM Sans", sans-serif;
    font-size: 0.65rem;
    font-weight: 500;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #e879f9;
  }

  div {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #e879f9;
    box-shadow: 0 0 8px #e879f9;
    animation: ${pulse} 1.5s ease-in-out infinite;
  }
`;

// Swipe-right indicator
const SwipeRight = styled.div`
  position: absolute;
  top: 50%;
  left: -70px;
  transform: translateY(-50%);
  opacity: ${({ op }) => op || 0};
  transition: opacity 0.4s;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
`;

const SwipeArrow = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #e879f9, #a855f7);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  box-shadow: 0 0 30px rgba(232, 121, 249, 0.5);
`;

const SwipeLabel = styled.div`
  font-family: "DM Sans", sans-serif;
  font-size: 0.6rem;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.4);
`;

// ── Right: text panel
const TextPanel = styled.div`
  margin-left: 7rem;
  max-width: 440px;
`;

const Eyebrow = styled.div`
  font-family: "DM Sans", sans-serif;
  font-size: 0.7rem;
  font-weight: 500;
  letter-spacing: 0.25em;
  text-transform: uppercase;
  color: #c084fc;
  margin-bottom: 1.25rem;
  opacity: 0;
  transform: translateY(20px);
`;

const Headline = styled.h2`
  font-family: "Cormorant Garamond", serif;
  font-size: clamp(3rem, 5vw, 4.5rem);
  font-weight: 300;
  line-height: 1.1;
  color: #fff;
  margin-bottom: 1.5rem;
  opacity: 0;
  transform: translateY(25px);

  em {
    font-style: italic;
    font-weight: 600;
    background: linear-gradient(90deg, #e879f9, #a855f7, #7c3aed, #e879f9);
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: ${shimmer} 4s linear infinite;
  }
`;

const Body = styled.p`
  font-family: "DM Sans", sans-serif;
  font-size: 1rem;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.45);
  line-height: 1.85;
  margin-bottom: 2.5rem;
  opacity: 0;
  transform: translateY(20px);
`;

const ProgressRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  opacity: 0;
  transform: translateY(20px);
`;

const ProgressItem = styled.div``;

const ProgressLabel = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.4rem;

  span:first-child {
    font-family: "DM Sans", sans-serif;
    font-size: 0.75rem;
    font-weight: 400;
    color: rgba(255, 255, 255, 0.5);
    letter-spacing: 0.05em;
  }

  span:last-child {
    font-family: "Cormorant Garamond", serif;
    font-size: 0.9rem;
    font-weight: 600;
    color: #c084fc;
  }
`;

const ProgressTrack = styled.div`
  height: 2px;
  background: rgba(255, 255, 255, 0.07);
  border-radius: 2px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${({ w }) => w};
  background: linear-gradient(90deg, #7c3aed, #e879f9);
  border-radius: 2px;
  transform: scaleX(0);
  transform-origin: left;
`;

// Counter showing which card is active
const CardCounter = styled.div`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 6px;
  align-items: center;
`;

const Dot = styled.div`
  width: ${({ active }) => (active ? "20px" : "6px")};
  height: 6px;
  border-radius: 3px;
  background: ${({ active }) =>
    active
      ? "linear-gradient(90deg, #a855f7, #e879f9)"
      : "rgba(255,255,255,0.15)"};
  transition:
    width 0.4s,
    background 0.4s;
`;

// ── Card data
const CARDS = [
  {
    name: "Ava, 24",
    meta: "Brooklyn · Designer",
    tags: ["Art", "Coffee", "Hiking"],
    bg: "linear-gradient(160deg, #1a0030 0%, #2d0055 40%, #4a1080 100%)",
    blur: "14px",
    darkness: 0.35,
    rot: "6deg",
    oy: "-8px",
    nameOp: 0.25,
    metaOp: 0.15,
  },
  {
    name: "Maya, 27",
    meta: "Manhattan · Architect",
    tags: ["Travel", "Jazz", "Yoga"],
    bg: "linear-gradient(160deg, #200020 0%, #400050 40%, #7020a0 100%)",
    blur: "9px",
    darkness: 0.22,
    rot: "-4deg",
    oy: "-4px",
    nameOp: 0.4,
    metaOp: 0.25,
  },
  {
    name: "Zoe, 25",
    meta: "Williamsburg · Photographer",
    tags: ["Film", "Music", "Surf"],
    bg: "linear-gradient(160deg, #150030 0%, #300060 40%, #6030b0 100%)",
    blur: "5px",
    darkness: 0.12,
    rot: "2deg",
    oy: "0px",
    nameOp: 0.6,
    metaOp: 0.4,
  },
  {
    name: "Sofia, 26",
    meta: "SoHo · Writer",
    tags: ["Books", "Wine", "Dance"],
    bg: "linear-gradient(160deg, #100025 0%, #280055 40%, #5828a8 70%, #8840c0 100%)",
    blur: "0px",
    darkness: 0,
    rot: "0deg",
    oy: "0px",
    nameOp: 1,
    metaOp: 1,
  },
];

const STATS = [
  { label: "Real photos only", value: "100%" },
  { label: "Match accuracy", value: "94%" },
  { label: "Meet within a week", value: "67%" },
];

export default function CardStackReveal() {
  const wrapperRef = useRef(null);
  const stickyRef = useRef(null);
  const cardRefs = useRef([]);
  const fillRefs = useRef([]);
  const dotRefs = useRef([]);
  const eyebrowRef = useRef(null);
  const headlineRef = useRef(null);
  const bodyRef = useRef(null);
  const statsRef = useRef(null);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Each card gets 1 full vh of scroll to peel off
    // Plus 1 vh at start to intro the stack, 1 vh hold on final card
    const totalCards = CARDS.length;
    const scrollDist = vh * (totalCards + 1.5);
    wrapper.style.height = `${scrollDist + vh}px`;

    // ── Text intro (scrubs in over first 30% of scroll)
    const textEls = [
      eyebrowRef.current,
      headlineRef.current,
      bodyRef.current,
      statsRef.current,
    ];
    gsap
      .timeline({
        scrollTrigger: {
          trigger: wrapper,
          start: "top top",
          end: `+=${vh * 0.6}`,
          scrub: 0.7,
        },
      })
      .to(eyebrowRef.current, { opacity: 1, y: 0, ease: "power2.out" }, 0)
      .to(headlineRef.current, { opacity: 1, y: 0, ease: "power2.out" }, 0.1)
      .to(bodyRef.current, { opacity: 1, y: 0, ease: "power2.out" }, 0.2)
      .to(statsRef.current, { opacity: 1, y: 0, ease: "power2.out" }, 0.3);

    // ── Progress bar fills
    fillRefs.current.forEach((fill, i) => {
      if (!fill) return;
      gsap
        .timeline({
          scrollTrigger: {
            trigger: wrapper,
            start: `+=${vh * (0.3 + i * 0.12)}`,
            end: `+=${vh * (0.6 + i * 0.12)}`,
            scrub: 0.8,
          },
        })
        .to(fill, { scaleX: 1, ease: "power2.out" });
    });

    // ── Card peeling: card[0] is bottom (index 0), card[3] is top (index 3)
    // We peel from the top card downward
    // Each card peels at scroll segment: vh * (1 + cardIndex) to vh * (2 + cardIndex)
    // Top card = index 3 peels first, then 2, then 1 (card 0 stays)

    const peelOrder = [3, 2, 1]; // indices to peel in order

    peelOrder.forEach((cardIdx, peelStep) => {
      const card = cardRefs.current[cardIdx];
      if (!card) return;

      const start = vh * (0.8 + peelStep * 1.1);
      const end = vh * (1.7 + peelStep * 1.1);

      // Direction alternates for visual interest
      const toX = cardIdx % 2 === 0 ? "130%" : "-130%";
      const toRot = cardIdx % 2 === 0 ? "25deg" : "-25deg";

      gsap
        .timeline({
          scrollTrigger: {
            trigger: wrapper,
            start: `+=${start}`,
            end: `+=${end}`,
            scrub: 0.9,
          },
        })
        .to(card, {
          x: toX,
          y: "-20%",
          rotation: toRot,
          opacity: 0,
          scale: 0.92,
          ease: "power2.inOut",
        });

      // As this card leaves, update dot indicator
      ScrollTrigger.create({
        trigger: wrapper,
        start: `+=${(start + end) / 2}`,
        onEnter: () => updateDots(3 - peelStep - 1),
        onLeaveBack: () => updateDots(3 - peelStep),
      });
    });

    function updateDots(activeIdx) {
      dotRefs.current.forEach((dot, i) => {
        if (!dot) return;
        // dots are rendered 0..3 matching card order bottom-to-top
        // activeIdx is which card is now on top
        dot.style.width = i === activeIdx ? "20px" : "6px";
        dot.style.background =
          i === activeIdx
            ? "linear-gradient(90deg,#a855f7,#e879f9)"
            : "rgba(255,255,255,0.15)";
      });
    }

    // Init dots — top card (index 3) is active first
    updateDots(3);

    return () => ScrollTrigger.getAll().forEach((t) => t.kill());
  }, []);

  return (
    <>
      <GlobalStyle />
      <Wrapper ref={wrapperRef}>
        <Sticky ref={stickyRef}>
          <Orb
            s="500px"
            c="rgba(124,58,237,0.2)"
            top="-10%"
            left="-8%"
            dur="6s"
            del="0s"
            op={0.35}
          />
          <Orb
            s="400px"
            c="rgba(232,121,249,0.15)"
            top="40%"
            right="-6%"
            dur="7s"
            del="1s"
            op={0.25}
          />
          <Orb
            s="300px"
            c="rgba(168,85,247,0.18)"
            bottom="-5%"
            left="35%"
            dur="5s"
            del="0.5s"
            op={0.3}
          />

          <Scene>
            {/* ── Card stack */}
            <StackArea>
              {CARDS.map((card, i) => (
                <Card
                  key={i}
                  ref={(el) => (cardRefs.current[i] = el)}
                  rot={card.rot}
                  oy={card.oy}
                  style={{ zIndex: i + 1 }}
                >
                  <CardBg bg={card.bg} />
                  <CardNoise />
                  <CardBlur b={card.blur} d={card.darkness} />
                  <CardOverlay />

                  {/* Top card extras */}
                  {i === CARDS.length - 1 && (
                    <MatchBadge op={1} ty="0px">
                      <div />
                      <span>Your Match</span>
                    </MatchBadge>
                  )}

                  <CardContent>
                    <CardName op={card.nameOp}>{card.name}</CardName>
                    <CardMeta op={card.metaOp}>{card.meta}</CardMeta>
                    <CardTags
                      op={i === CARDS.length - 1 ? 1 : 0}
                      ty={i === CARDS.length - 1 ? "0px" : "8px"}
                    >
                      {card.tags.map((t, j) => (
                        <Tag key={j}>{t}</Tag>
                      ))}
                    </CardTags>
                  </CardContent>
                </Card>
              ))}

              {/* Swipe indicator on the left */}
              <SwipeRight op={0.85}>
                <SwipeArrow>❤️</SwipeArrow>
                <SwipeLabel>It's a match</SwipeLabel>
              </SwipeRight>

              {/* Dot progress */}
              <CardCounter>
                {CARDS.map((_, i) => (
                  <Dot key={i} ref={(el) => (dotRefs.current[i] = el)} />
                ))}
              </CardCounter>
            </StackArea>

            {/* ── Right text */}
            <TextPanel>
              <Eyebrow ref={eyebrowRef}>Find the one</Eyebrow>
              <Headline ref={headlineRef}>
                Each swipe gets
                <br />
                <em>closer</em> to real.
              </Headline>
              <Body ref={bodyRef}>
                No filters. No illusions. Just real people, real photos — each
                one sharper and more vivid than the last. The one you're looking
                for is right at the bottom of the stack.
              </Body>
              <ProgressRow ref={statsRef}>
                {STATS.map((s, i) => (
                  <ProgressItem key={i}>
                    <ProgressLabel>
                      <span>{s.label}</span>
                      <span>{s.value}</span>
                    </ProgressLabel>
                    <ProgressTrack>
                      <ProgressFill
                        ref={(el) => (fillRefs.current[i] = el)}
                        w={s.value}
                      />
                    </ProgressTrack>
                  </ProgressItem>
                ))}
              </ProgressRow>
            </TextPanel>
          </Scene>
        </Sticky>
      </Wrapper>
    </>
  );
}
